import { OptionsApi } from '@core/swagger-client';
import { CompositePageHeader, ContainerNoTopSpace, ErrorMessagePage, Heading, LoaderPage, PageHeader, PageHeaderContent } from '@core/ui';
import { useParams } from '@online-webinar/router';
import { BackToOriginLink } from '@online-webinar/ui-frontend';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContactDetails } from '../../atoms';
import { ContactBody, ContactCard } from '../../molecules';
import { Contact, ContactList } from '../../organisms';

export const LiveParticipants = () => {
    const { webinarId } = useParams({ strict: false });
    const { t } = useTranslation();

    const [contacts, setContacts] = useState<Contact[]>([]);
    const { data: participants, isLoading, error: participantsError } = useQuery(OptionsApi.insights.getLiveParticipants(webinarId!));
    const { data: webinar, isLoading: isWebinarLoading, error: webinarError } = useQuery(OptionsApi.webinar.get(webinarId!, { staleTime: 60_000 }));

    useEffect(() => {
        if (!participants) {
            return;
        }

        setContacts(
            participants.map(
                (account) =>
                    ({
                        emailAddress: account.emailAddress,
                        nickname: account.nickname,
                        firstName: account.firstName,
                        lastName: account.lastName,
                        companyName: account.companyName,
                        country: account.countryCode,
                        firstOnline: account.firstOnlineLive,
                        lastOnline: account.lastOnlineLive,
                        timeOnline: account.timeOnlineLive,
                        percentageOnline: account.percentageOnlineLive
                    }) as Contact
            )
        );
    }, [participants]);

    const error = participantsError ?? webinarError;
    if (error) {
        return <ErrorMessagePage apiError={error} />;
    }

    if (isWebinarLoading || !webinar || isLoading || !contacts) {
        return <LoaderPage />;
    }

    return (
        <ContainerNoTopSpace>
            <ContactCard>
                <PageHeader>
                    <PageHeaderContent>
                        <CompositePageHeader title={webinar.title} broadcastDateTime={webinar.broadcastDateTime} />
                    </PageHeaderContent>
                </PageHeader>

                <ContactBody>
                    <BackToOriginLink fallback="insights" />
                    <Heading variant="h1" classNames="mb-4 lg:mb-6 2xl:mb-8">
                        {t('Insights.Live.PageHeading')}
                    </Heading>
                    <ContactDetails
                        contactCount={contacts.length}
                        singleText={t('Insights.Live.AmountText.Single')}
                        multipleText={t('Insights.Live.AmountText.Multiple')}
                    />
                    <ContactList
                        contacts={contacts}
                        showTimeOnline
                        webinarTitle={webinar.title}
                        exportFileName="LiveParticipants.xlsx"
                        exportTitle="Live participants"
                    />
                </ContactBody>
            </ContactCard>
        </ContainerNoTopSpace>
    );
};
